import { getColor } from '@chakra-ui/theme-tools';
import { useState } from 'react';
import { connect } from 'react-redux';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';
import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Popover,
  PopoverBody,
  PopoverButtonGroup,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Radio,
  RadioGroup,
  NumberInput,
  NumberInputField,
  HStack,
  Text,
  useDisclosure,
  theme,
  GearIcon,
} from '@frontend/components';

import { DEFAULT_UNITS_BY_PREFERRED_SYSTEM } from 'constants/unitOfMeasure';

import { cadViewer } from 'modules/common/viewer/cadViewer/data/actions';
import {
  getDimensionalPrecision,
  getRenderNativeColors,
} from 'modules/common/viewer/cadViewer/data/reducer';

import { useUnitSystemPreference } from '../addOns/hooks';

const connector = connect(
  (state) => ({
    dimensionalPrecision: getDimensionalPrecision(state),
    renderNativeColors: getRenderNativeColors(state),
  }),
  {
    receiveDisplayUnits: cadViewer.receiveDisplayUnits,
    receiveDimensionalPrecision: cadViewer.receiveDimensionalPrecision,
    setRenderNativeColors: cadViewer.setRenderNativeColors,
  },
);

const inferDisplayUnits = (preferredUnitSystem: PreferredUnitSystem) =>
  DEFAULT_UNITS_BY_PREFERRED_SYSTEM[preferredUnitSystem];

const DisplayOptionsPopover = ({
  dimensionalPrecision,
  renderNativeColors,
  receiveDisplayUnits,
  receiveDimensionalPrecision,
  setRenderNativeColors,
}) => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { preferredUnitSystem, setPreferredUnitSystem } =
    useUnitSystemPreference();

  const [candidateDisplayUnitSystem, setCandidateDisplayUnitSystem] =
    useState(preferredUnitSystem);

  const [candidateRenderNativeColors, _setCandidateRenderNativeColors] =
    useState(renderNativeColors);

  const [candidateDimensionalPrecision, setCandidateDimensionalPrecision] =
    useState(dimensionalPrecision);

  const setCandidateRenderNativeColors = (value: boolean | string) => {
    _setCandidateRenderNativeColors(value === 'true' || value === true);
  };

  const closeAndRevert = () => {
    setCandidateDisplayUnitSystem(preferredUnitSystem);
    setCandidateRenderNativeColors(renderNativeColors);
    setCandidateDimensionalPrecision(dimensionalPrecision);
    onClose();
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={closeAndRevert}
      onOpen={onOpen}
      autoFocus
      size="sm"
      placement="bottom-end"
    >
      <PopoverTrigger>
        <IconButton boxSize={3} color={getColor(theme, 'feature.secondary')}>
          <GearIcon />
        </IconButton>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverHeader>Display Options</PopoverHeader>
        <PopoverBody>
          <FormControl mb={1.5}>
            <FormLabel>Units</FormLabel>
            <RadioGroup
              value={candidateDisplayUnitSystem}
              size="sm"
              onChange={(value) => {
                setCandidateDisplayUnitSystem(value);
                // clamp Dimensional Precision value if changed to Metric
                if (value === PREFERRED_UNIT_SYSTEMS.METRIC) {
                  if (candidateDimensionalPrecision > 4) {
                    setCandidateDimensionalPrecision(4);
                  }
                }
              }}
              alignItems="left"
            >
              <Radio value={PREFERRED_UNIT_SYSTEMS.IMPERIAL}>Imperial</Radio>
              <Radio value={PREFERRED_UNIT_SYSTEMS.METRIC}>Metric</Radio>
            </RadioGroup>
          </FormControl>
          <FormControl mb={1.5}>
            <FormLabel>Colors</FormLabel>
            <RadioGroup
              value={candidateRenderNativeColors}
              size="sm"
              onChange={(value) => setCandidateRenderNativeColors(value)}
              alignItems="left"
            >
              <Radio value={false}>Model</Radio>
              <Radio value={true}>Default</Radio>
            </RadioGroup>
          </FormControl>
          <FormControl mb={1.5}>
            <FormLabel>Dimensional Precision</FormLabel>
            <HStack isInline={true}>
              <Text fontSize="sm">Decimal Places</Text>
              <NumberInput
                clampValueOnBlur
                focusInputOnChange
                // 4 decimal precision for metric, 6 for imperial
                // We are clamping to the equivalent of 0.5 micron. 1 micron is 0.001 mm.
                // The finest precision someone would make a part is 0.5 micron.
                // As a side note, 1 thou is 25 micron.
                // When we specify precision to say 0.0001 inch, we are saying 2.5 microns.
                // 0.5 microns is even finer which agrees with 4 decimals if units is mm.
                max={
                  candidateDisplayUnitSystem === PREFERRED_UNIT_SYSTEMS.METRIC
                    ? 4
                    : 6
                }
                min={1}
                step={1}
                value={candidateDimensionalPrecision}
                onChange={(value) => setCandidateDimensionalPrecision(value)}
                pattern="[0-9]*(.[0-9]+)?"
                size={'sm'}
                width="auto"
              >
                <NumberInputField width={6} />
              </NumberInput>
            </HStack>
          </FormControl>
        </PopoverBody>
        <PopoverFooter>
          <PopoverButtonGroup>
            <Button variant="secondary" onClick={closeAndRevert}>
              cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (candidateDisplayUnitSystem !== preferredUnitSystem) {
                  receiveDisplayUnits({
                    displayUnits: inferDisplayUnits(candidateDisplayUnitSystem),
                  });
                  // Update the user's personal unit system preference
                  setPreferredUnitSystem(candidateDisplayUnitSystem);
                }
                if (candidateDimensionalPrecision !== dimensionalPrecision) {
                  receiveDimensionalPrecision({
                    dimensionalPrecision: candidateDimensionalPrecision,
                  });
                }
                if (candidateRenderNativeColors !== renderNativeColors) {
                  setRenderNativeColors({
                    renderNativeColors: candidateRenderNativeColors,
                  });
                }
                onClose();
              }}
              isDisabled={
                candidateDisplayUnitSystem === preferredUnitSystem &&
                candidateDimensionalPrecision === dimensionalPrecision &&
                candidateRenderNativeColors === renderNativeColors
              }
            >
              apply
            </Button>
          </PopoverButtonGroup>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

export default connector(DisplayOptionsPopover);
